import { useEffect, useRef, useCallback } from 'react';

/** Browser events that count as "user activity" and reset the idle timer. */
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
    'mousedown',
    'mousemove',
    'keydown',
    'scroll',
    'touchstart',
    'wheel',
];

/** How often to check elapsed time against the timeout (ms). */
const CHECK_INTERVAL_MS = 1000;

interface UseIdleTimeoutOptions {
    /** Idle duration (ms) before `onIdle` fires. */
    timeoutMs: number;
    /** Called once the user has been inactive for `timeoutMs`. */
    onIdle: () => void;
    /** Set to false to disable the timer entirely (e.g. user not authenticated). */
    enabled?: boolean;
}

/**
 * Tracks user activity (mouse, keyboard, touch, scroll) and invokes `onIdle`
 * once no activity has been seen for `timeoutMs`. Any activity resets the timer.
 *
 * Uses a wall-clock timestamp (rather than relying solely on setTimeout
 * accuracy) so the check remains correct even when the tab is backgrounded
 * and browsers throttle timers � a background tab may check less often, but
 * will still correctly detect that `timeoutMs` has elapsed since last activity.
 */
export const useIdleTimeout = ({ timeoutMs, onIdle, enabled = true }: UseIdleTimeoutOptions): void => {
    const lastActivityRef = useRef<number>(Date.now());
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const onIdleRef = useRef(onIdle);
    const firedRef = useRef(false);

    // Keep the latest callback without re-registering listeners every render.
    useEffect(() => {
        onIdleRef.current = onIdle;
    }, [onIdle]);

    const recordActivity = useCallback(() => {
        lastActivityRef.current = Date.now();
        firedRef.current = false;
    }, []);

    useEffect(() => {
        if (!enabled) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        recordActivity();

        ACTIVITY_EVENTS.forEach((event) => {
            window.addEventListener(event, recordActivity, { passive: true });
        });

        intervalRef.current = setInterval(() => {
            const elapsed = Date.now() - lastActivityRef.current;
            if (elapsed >= timeoutMs && !firedRef.current) {
                firedRef.current = true;
                onIdleRef.current();
            }
        }, CHECK_INTERVAL_MS);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            ACTIVITY_EVENTS.forEach((event) => {
                window.removeEventListener(event, recordActivity);
            });
        };
    }, [enabled, timeoutMs, recordActivity]);
};